import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Lock, HandHeart } from "lucide-react";

const MV = [
  { key: "trust", title: "Trust" },
  { key: "follow", title: "Follow" },
  { key: "imitate", title: "Imitate" },
  { key: "teach", title: "Teach Others" },
];

const Bar = ({ value, max = 25 }) => (
  <div className="h-1.5 bg-secondary/60 overflow-hidden">
    <div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${(value / max) * 100}%` }} />
  </div>
);

function AvgBlock({ label, block, min, testid }) {
  return (
    <div data-testid={testid} className="border border-border bg-card p-5">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-xs text-muted-foreground" data-testid={`${testid}-count`}>{block.count} completed</div>
      </div>
      {block.averages ? (
        <>
          <div className="font-serif text-3xl mt-2 tabular-nums" data-testid={`${testid}-overall`}>
            {block.averages.overall}<span className="text-sm text-muted-foreground"> / 100 avg</span>
          </div>
          <div className="mt-4 space-y-3">
            {MV.map((m) => (
              <div key={m.key}>
                <div className="flex justify-between text-sm"><span>{m.title}</span>
                  <span className="tabular-nums text-muted-foreground">{block.averages.movements[m.key].score} / 25</span></div>
                <Bar value={block.averages.movements[m.key].score} />
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground italic flex items-center gap-2" data-testid={`${testid}-hidden`}>
          <Lock className="w-3.5 h-3.5" /> Averages appear once {min} or more members have finished, to protect privacy.
        </p>
      )}
    </div>
  );
}

function SharedMember({ m }) {
  const [open, setOpen] = useState(false);
  const s = m.start?.scores, f = m.finish?.scores;
  return (
    <li className="border-b border-border py-4" data-testid={`shared-member-${m.user_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left"
        data-testid={`shared-member-toggle-${m.user_id}`}>
        <span className="font-medium">{m.name}</span>
        <span className="flex items-center gap-3 text-sm tabular-nums text-muted-foreground">
          {s ? s.overall : "—"} <span>→</span> <span className="text-primary">{f ? f.overall : "—"}</span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="mt-4 space-y-2 text-sm">
          {MV.map((mv) => (
            <div key={mv.key} className="flex justify-between">
              <span>{mv.title}</span>
              <span className="tabular-nums text-muted-foreground">
                {s ? s.movements[mv.key].score : "—"} → {f ? f.movements[mv.key].score : "—"}
              </span>
            </div>
          ))}
          {[m.start, m.finish].filter(Boolean).flatMap((a) => Object.entries(a.reflections || {}))
            .filter(([, v]) => v).map(([k, v]) => (
              <p key={k} className="border-l-2 border-primary/40 pl-3 italic text-muted-foreground whitespace-pre-wrap">{v}</p>
            ))}
        </div>
      )}
    </li>
  );
}

function PendingList({ groupId, items }) {
  const [rows, setRows] = useState(items);
  const nudge = async (uid) => {
    try {
      const { data } = await api.post(`/groups/${groupId}/nudge`, { user_id: uid });
      setRows((r) => r.map((x) => (x.user_id === uid ? { ...x, can_nudge: false, last_nudged_at: data.last_nudged_at } : x)));
      toast.success("Gentle reminder sent.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the reminder.");
    }
  };
  if (!rows.length) return null;
  return (
    <div className="mt-10" data-testid="group-pending-start">
      <h3 className="font-serif text-xl mb-1">Haven't started yet</h3>
      <p className="text-sm text-muted-foreground mb-3">Send a gentle in-app reminder. One nudge per person every 3 days.</p>
      <ul>
        {rows.map((m) => (
          <li key={m.user_id} className="flex items-center justify-between border-b border-border py-3" data-testid={`pending-member-${m.user_id}`}>
            <span>{m.name}</span>
            {m.can_nudge ? (
              <button onClick={() => nudge(m.user_id)} data-testid={`nudge-btn-${m.user_id}`}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors">
                <HandHeart className="w-4 h-4" /> Nudge
              </button>
            ) : (
              <span className="text-xs text-muted-foreground" data-testid={`nudge-sent-${m.user_id}`}>
                Reminded {new Date(m.last_nudged_at).toLocaleDateString()}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export const GroupAssessmentPanel = ({ groupId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/groups/${groupId}/assessments`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [groupId]);
  if (!data) return null;
  return (
    <div className="mt-14" data-testid="group-assessment-panel">
      <h2 className="font-serif text-2xl mb-6 flex items-center gap-4">
        <span>Spiritual Health</span><span className="flex-1 rule-line mb-1"></span>
      </h2>
      <div className="grid sm:grid-cols-2 gap-5">
        <AvgBlock label="Week 1 · Starting" block={data.summary.start} min={data.min_for_averages} testid="group-avg-start" />
        <AvgBlock label="Week 11 · Finishing" block={data.summary.finish} min={data.min_for_averages} testid="group-avg-finish" />
      </div>
      <PendingList groupId={groupId} items={data.pending_start || []} />
      <h3 className="font-serif text-xl mt-10 mb-2">Shared with you</h3>
      {data.shared.length ? (
        <ul data-testid="group-shared-list">{data.shared.map((m) => <SharedMember key={m.user_id} m={m} />)}</ul>
      ) : (
        <p className="text-sm text-muted-foreground italic" data-testid="group-shared-empty">
          No one has chosen to share their results yet. Members can opt in from their results page.
        </p>
      )}
    </div>
  );
};
